import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  AgBarSeriesOptions,
  AgCartesianSeriesOptions
} from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { keyBy } from 'lodash';
import { useEffect } from 'react';

import RadialGaugeChart from '@/components/charts/RadialGaugeChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportsTemplateCompareWidget from '@/features/ReportsTemplateCompareWidget/ReportsTemplateCompareWidget';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import type {
  ServiceUsagePerDayDTO,
  UsageLimitType
} from '@/pages/Dashboard/Dashboard.types';
import { formatUsageGaugeText } from '@/pages/Dashboard/Dashboard.utils';

import {
  reportsEndDateAtom,
  reportsNetworkAvgAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom,
  reportsUsageDataAtom,
  reportsUsageLimitsDataAtom
} from '../../ReportsTemplate.hooks';
import useStyles from '../ReportsPages.styles';

const NetworkUsagePage = () => {
  const theme = useTheme();
  const { classes } = useStyles();
  const [usageData] = useAtom(reportsUsageDataAtom);
  const [usageLimitsData] = useAtom(reportsUsageLimitsDataAtom);
  const [timeframe] = useAtom(reportsTimeFrameAtom);
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const [, setNetworkAvg] = useAtom(reportsNetworkAvgAtom);

  // Min time frame is set to 7 days if the selected timeframe is less than 7 days
  // Only to be used for data over time graphs
  const minTimeFrame = timeframe < 7 ? 7 : timeframe;

  const networkUsageLimit = usageLimitsData?.find(
    (item: UsageLimitType) => item.active && item.product_type === 'NET'
  )?.subscription_limit;

  // Used for network usage over time graph (includes 7 days of data)
  const networkData = usageData?.filter(
    (row: ServiceUsagePerDayDTO) => row.type === 'net'
  );

  // Used for other graphs and widgets
  const networkDataInTimeframe = networkData?.filter(
    (row: ServiceUsagePerDayDTO) => {
      const rowDate = dayjs(row.date);
      return (
        rowDate.isSame(dayjs(startDate), 'day') ||
        rowDate.isSame(dayjs(endDate), 'day') ||
        (rowDate.isAfter(dayjs(startDate), 'day') &&
          rowDate.isBefore(dayjs(endDate), 'day'))
      );
    }
  );

  const total = Array.isArray(networkDataInTimeframe)
    ? networkDataInTimeframe.reduce(
        (acc: number, curr: ServiceUsagePerDayDTO) => acc + curr.mb_per_second,
        0
      )
    : 0;

  const networkAverage = total / timeframe;

  const networkUsageGraphData = () => {
    const networkUsagePerDay = keyBy(networkData, 'date');
    const usage = [];

    for (let i = 0; i <= minTimeFrame - 1; i += 1) {
      const date = `${dayjs(endDate)
        .subtract(minTimeFrame - 1 - i, 'days')
        .format(YEAR_MONTH_DAY_FORMAT)}`;

      // If the date is not in the data, we assume 0 usage for that day
      const count = networkUsagePerDay[date]
        ? networkUsagePerDay[date]?.mb_per_second
        : 0;
      usage.push({
        date: dayjs(date).toDate(),
        usage: count,
        limit: networkUsageLimit
      });
    }

    return usage;
  };

  const roundedNetworkAverage = Math.ceil(networkAverage * 100) / 100;

  useEffect(() => {
    setNetworkAvg(roundedNetworkAverage);
  }, [roundedNetworkAverage]);

  const networkIngestionPercent = networkUsageLimit
    ? (roundedNetworkAverage / networkUsageLimit) * 100
    : 0;

  const usageBarLineSeries: AgCartesianSeriesOptions[] = [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'usage',
      yName: 'Usage',
      itemStyler: (params: { datum: { usage: number; limit: number } }) => {
        let fill = theme.palette.info.main;
        if (params.datum.usage > params.datum.limit) {
          fill = theme.palette.warning.main;
        }
        return { fill };
      }
    },
    {
      type: 'line',
      xKey: 'date',
      yKey: 'limit',
      yName: 'License Limit',
      stroke: theme.palette.error.main,
      lineDash: [8, 8],
      marker: {
        enabled: false
      }
    }
  ];

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>NETWORK USAGE</ReportsTemplateSectionTitle>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.networkUsageDescription}
      </ReportParagraph>
      <Stack sx={{ height: 250 }} direction="row" spacing={2}>
        <Box sx={{ width: '35%' }}>
          <ReportsTemplateBoxContainer height={117}>
            <ReportsTemplateCompareWidget
              title="Licensed Limit"
              value={`${networkUsageLimit} Mbps`}
            />
          </ReportsTemplateBoxContainer>
          <div style={{ marginTop: '16px' }}>
            <ReportsTemplateBoxContainer height={117}>
              <ReportsTemplateCompareWidget
                title="Daily Average"
                value={`${roundedNetworkAverage} Mbps`}
              />
            </ReportsTemplateBoxContainer>
          </div>
        </Box>
        <Box
          data-testid="network-usage-radial-gauge"
          sx={{ maxHeight: 250, flexGrow: 1 }}
        >
          <RadialGaugeChart
            description={formatUsageGaugeText(
              timeframe,
              roundedNetworkAverage,
              networkIngestionPercent,
              'network',
              networkUsageLimit || 0
            )}
            limit={networkUsageLimit || 0}
            orientation="vertical"
            percent={parseFloat(roundedNetworkAverage.toString())}
            primaryLabel={roundedNetworkAverage.toString()}
            secondaryLabel="Mbps"
            sizeFontsForReports
            title={`Trailing ${timeframe}-day average`}
          />
        </Box>
      </Stack>
      <Box
        data-testid="network-usage-graph"
        sx={{ height: 300 }}
        className={classes.reportsChartContainer}
      >
        <Typography className={classes.reportsPreviewSubTitle}>
          {`Network Bandwidth Utilization Over ${minTimeFrame} Days (Mbps)`}
        </Typography>
        <VerticalBarChart
          data={networkUsageGraphData()}
          series={usageBarLineSeries as AgBarSeriesOptions[]}
          suppressExponentialAxis
          suppressDoubleAxis
          suppressLegend
        />
      </Box>
      <ReportParagraph fontSize="10px">
        {REPORT_PARAGRAPHS.networkUsageGraphDescription}
      </ReportParagraph>
    </div>
  );
};

export default NetworkUsagePage;
